import { ButtonStyle, FetchStatus, IEntity, PageMode } from "../../../../../base/types"
import { IServiceRequest, ServiceRequestPaymentStage } from "../../models"
import { useCallback, useEffect, useState } from "react"
import { IPageLink } from "../../../../../base/models"
import clsx from "clsx"
import { useAuth } from "../../../../../../modules/auth"
import { useFormikContext } from "formik"
import { useDataContext, usePageContext } from "../../../../page/context"
import { useServiceRequestContext } from "../../service-request"
import { AMBankButton, FlywireButton, FPXBankButton } from "../../../pg-integration"
import { IFieldValue } from "../../../../../services/models"
import { useCreateMutation } from "../../../../../services/data/tableDataApi"
import { useUploadDocument } from "../../../../../services/data/sharepoint"
import { useCreateEntityChecklistItemMutation } from "../../../document-checklist/services/entityChecklistItemsApi"
import { Spinner } from "../../../../../widgets"
import { KTSVG } from "../../../../../../helpers"

export const MoneyAttribute = ({
    attributeId,
    value,
    relativePath,
    className,
    onChange,
    onBlur,
}: {
    attributeId: string,
    value?: string,
    relativePath?: string,
    required?: boolean,
    placeholder?: string,
    readOnly?: boolean,
    className?: string,
    onChange: (value: any) => void,
    onBlur: () => void
}) => {
    const [paymentMode, setPaymentMode] = useState<string>('online');
    const { result } = useServiceRequestContext();
    const { serviceRequest } = result || {};

    return (
        <div className="mt-5">
            <div className="d-flex align-items-center card-header ">
                <h3>Select Payment Mode</h3>
            </div>
            <div className="card-body">
                <div className="form-check">
                    <input
                        type="radio"
                        id="online"
                        name="paymentMode"
                        value="online"
                        className="form-check-input"
                        checked={paymentMode === 'online'}
                        onChange={() => setPaymentMode('online')}
                    />
                    <label htmlFor="online" className="form-check-label">
                        Online
                    </label>
                </div>
                {serviceRequest?.paymentStage !== ServiceRequestPaymentStage.Completed && (
                    <div className="form-check mt-2">
                        <input
                            type="radio"
                            id="offline"
                            name="paymentMode"
                            value="offline"
                            className="form-check-input"
                            checked={paymentMode === 'offline'}
                            onChange={() => setPaymentMode('offline')}
                        />
                        <label htmlFor="offline" className="form-check-label">
                            Offline
                        </label>
                    </div>
                )}

                <div>{paymentMode === 'online' ?
                    <OnlinePayment attributeId={attributeId} onChange={onChange} onBlur={onBlur} paymentMode={paymentMode} /> :
                    <OfflinePayment attributeId={attributeId} paymentMode={paymentMode} relativePath={relativePath} />}</div>
            </div>
        </div>
    );
};

const OnlinePayment = ({
    attributeId,
    value,
    className,
    onChange,
    onBlur,
    paymentMode,
}: {
    attributeId: string,
    value?: string,
    required?: boolean,
    placeholder?: string,
    readOnly?: boolean,
    className?: string,
    onChange: (value: any) => void,
    onBlur: () => void,
    paymentMode: string,
}) => {
    const { page } = usePageContext()
    const { user } = useAuth()
    const { entity, result } = useServiceRequestContext();
    const { service, serviceRequest } = result || {}
    const { setFieldValue } = useFormikContext<IEntity>() || {}

    const currency = "INR";

    // NEW STATES
    const [showFPX, setShowFPX] = useState(false);
    const [isForeign, setIsForeign] = useState(false);

    useEffect(() => {
        if (!attributeId) return;

        const fieldError = isChecklistValid()
        if (fieldError && paymentMode === 'online') {
            setFieldValue(attributeId, false)
        } else {
            setFieldValue(attributeId, "")
        }
        // eslint-disable-next-line
    }, [serviceRequest, paymentMode]);

    const isChecklistValid = useCallback(() => {
        if (!serviceRequest)
            return;

        if (serviceRequest.paymentStage !== ServiceRequestPaymentStage.Completed) {
            return "Payment is not completed.";
        }

        // eslint-disable-next-line
    }, [serviceRequest])

    if (!user || !service || !serviceRequest || !service.paidService || !page) {
        return <></>
    }

    const amount = service.amount || 0;
    const orderNo = serviceRequest.requestNumber;
    const successPage: IPageLink = { pageId: page.id, systemId: serviceRequest.id, pageMode: PageMode.View }
    const failedPage: IPageLink = { pageId: page.id, systemId: serviceRequest.id, pageMode: PageMode.View }

    if (serviceRequest.paymentStage && serviceRequest.paymentStage !== ServiceRequestPaymentStage.Failed) {
        return <PaymentStatus
            serviceRequest={serviceRequest}
            className={className}
            amount={amount} />
    }

    return (
        <>
            {/* FOREIGN CURRENCY OPTION */}
            <div className="mt-3 mb-3">

                <label className="form-label fw-semibold text-danger fst-italic">
                    Do you want to pay in foreign currency?
                </label>

                <div className="d-flex gap-2">

                    <div className="form-check form-check-inline">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="onlineCurrency"
                            checked={!isForeign}
                            onChange={() => {
                                setIsForeign(false);
                                setShowFPX(false);
                            }}
                        />

                        <label className="form-check-label">
                            No
                        </label>
                    </div>

                    <div className="form-check form-check-inline">
                        <input
                            type="radio"
                            className="form-check-input"
                            name="onlineCurrency"
                            checked={isForeign}
                            onChange={() => {
                                setIsForeign(true);
                                setShowFPX(false);
                            }}
                        />

                        <label className="form-check-label">
                            Yes
                        </label>
                    </div>

                </div>
            </div>

            <div className={clsx(className, "d-flex flex-column gap-1 my-5")}>

                <div className="flex-fill fs-6 fw-semibold text-gray-600">
                    Pay{" "}
                    <span className="fs-2 fw-bolder text-gray-800">
                        {formatMoney(amount)}
                    </span>{" "}
                    with:
                </div>

                <div className="flex-fill d-flex flex-wrap align-items-stretch gap-2 mt-4">

                    {/* FOREIGN PAYMENT */}
                    {isForeign ? (
                        <>
                            <FlywireButton
                                className="border border-primary min-w-150px min-h-50px"
                                style={ButtonStyle.OutlinePrimary}
                                enrolmentNo={user.userName}
                                orderNo={orderNo}
                                entity={entity}
                                entityId={serviceRequest.id}
                                firstName={user.firstName || user.userName}
                                lastName={user.lastName}
                                email={user.email}
                                phoneNumber={user.phoneNumber}
                                successPage={successPage}
                                failedPage={failedPage}
                                amount={amount}
                                tutionFee={amount}
                                hostelFee={0}
                                otherFee={0}
                                currency={currency}
                            />
                        </>
                    ) : (
                        <>
                            {/* AM BANK */}
                            <AMBankButton
                                className="border border-primary min-w-150px min-h-50px"
                                style={ButtonStyle.OutlinePrimary}
                                enrolmentNo={user.userName}
                                orderNo={orderNo}
                                entity={entity}
                                entityId={serviceRequest.id}
                                firstName={user.firstName || user.userName}
                                lastName={user.lastName}
                                email={user.email}
                                phoneNumber={user.phoneNumber}
                                successPage={successPage}
                                failedPage={failedPage}
                                amount={amount}
                                currency={currency}
                            />

                            {/* FPX BUTTON */}
                            {!showFPX && (
                                <button
                                    className="border border-primary min-w-150px min-h-50px btn btn-light"
                                    onClick={() => setShowFPX(true)}
                                >
                                    <img
                                        src="/media/logos/FPX_Logo_FA_Full_FC.png"
                                        alt="FPX"
                                        className="h-25px"
                                    />
                                </button>
                            )}
                        </>
                    )}

                </div>
            </div>

            {/* FPX DROPDOWN */}
            {!isForeign && showFPX && (
                <div className="mt-4">

                    <button
                        className="btn btn-sm btn-light mb-3"
                        onClick={() => setShowFPX(false)}
                    >
                        ← Back
                    </button>

                    <FPXBankButton
                        className="border border-primary min-w-150px min-h-50px"
                        style={ButtonStyle.OutlinePrimary}
                        enrolmentNo={user.userName}
                        orderNo={orderNo}
                        entity={entity}
                        entityId={serviceRequest.id}
                        firstName={user.firstName || user.userName}
                        lastName={user.lastName}
                        email={user.email}
                        phoneNumber={user.phoneNumber}
                        successPage={successPage}
                        failedPage={failedPage}
                        amount={amount}
                        currency={currency}
                    />
                </div>
            )}
        </>
    )
}

const OfflinePayment = ({
    attributeId,
    className,
    paymentMode,
    relativePath
}: {
    attributeId: string;
    required?: boolean;
    placeholder?: string;
    readOnly?: boolean;
    className?: string;
    relativePath?: string;
    paymentMode: string;
}) => {
    const { user, siteId } = useAuth();
    const { result, entity } = useServiceRequestContext();
    const { service, serviceRequest } = result || {};
    const { setFieldValue } = useFormikContext<IEntity>() || {};
    const { setData } = useDataContext();
    const [insertRecord] = useCreateMutation();
    const [formData, setFormData] = useState({
        slcm_amount: "",
        slcm_paidon: "",
        slcm_receiptno: "",
        attachment: null,
    });
    const [isReadonly, setIsReadonly] = useState(false);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);
    const [isConfirmHidden, setIsConfirmHidden] = useState(false);
    const [isRecordCreated, setIsRecordCreated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [documentUrl, setDocumentUrl] = useState<string>("");
    const { uploadDocument } = useUploadDocument()
    const [createRecord] = useCreateEntityChecklistItemMutation()
    const [isUploading, setIsUploading] = useState(false);
    const [errors, setErrors] = useState({
        slcm_amount: "",
        slcm_paidon: "",
        slcm_receiptno: "",
        attachment: "",
    });

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        setErrors({ ...errors, [name]: "" });
    };

    const handleUpload = (file: File): Promise<string> => {
        if (!relativePath)
            return Promise.reject('Cant upload this document.');


        return uploadDocument({ file, folderRelativeUrl: relativePath })
            .then((document) => {
                return createRecord({
                    entityType: entity,
                    entityId: serviceRequest?.id,
                    documentUrl: document.serverAbsoluteUrl || document.serverRelativeUrl,
                })
                    .then(() => Promise.resolve(document.serverAbsoluteUrl))
                    .catch(() => Promise.reject('Cant upload this document.'));
            })
            .catch((error) => {
                console.error(error);
                return Promise.reject(error);
            });
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, files } = e.target;
        if (files) {
            setFormData((prev) => ({
                ...prev,
                [name]: files[0],
            }));

            if (!files || files.length === 0) {
                e.target.value = ""; // clear input
                setIsUploading(false);
                return;
            }

            setIsUploading(true);
            handleUpload(files[0])
                .then((data) => {
                    setDocumentUrl(data)
                    setErrors({ ...errors, [name]: "" });
                })
                .catch((error) => {
                    setErrors({ ...errors, [name]: "Error uploading document" });
                    console.error("Upload failed:", error);
                }).finally(() => setIsUploading(false));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }
    };

    const validateForm = () => {
        const newErrors: any = {};

        // if (!formData.slcm_amount) newErrors.slcm_amount = "Amount is required";
        if (!formData.slcm_paidon) newErrors.slcm_paidon = "Date is required";
        if (!formData.slcm_receiptno) newErrors.slcm_receiptno = "Receipt No is required";
        if (!formData.attachment) newErrors.attachment = "Attachment is required";

        setErrors(newErrors);

        return Object.keys(newErrors).length === 0;
    };

    const createPaymentRecord = (data: any) => {
        setLoading(true)
        saveFormData(data)
            .then(data => {
                setData({ type: "Card", result: data, status: FetchStatus.Success });
                setIsReadonly(true);
                setIsConfirmHidden(true);
                setStatusMessage("You can submit your request.");
                setIsRecordCreated(true);
            })
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setStatusMessage(message);
            }).finally(() => setLoading(false));
    };

    const saveFormData = (record: IEntity): Promise<IEntity> => {
        const fields: IFieldValue[] = Object.entries(record)
            .filter(([_, value]) => value !== undefined)
            .map(([field, value]) => ({ field, value, validate: true }));

        return insertRecord({ portalId: siteId || '', userId: user?.contactId, tableId: "slcm_payment", data: fields, triggers: true })
            .unwrap()
            .catch(err => {
                const message = err.data.message || 'An error occurred while processing your request. Please try again later.';
                setStatusMessage(message);
                return Promise.reject(message);
            });
    };

    const handleConfirm = () => {
        if (!validateForm()) return;

        const paymentData = {
            slcm_rollno: user?.contactId,
            slcm_amount: amount,
            slcm_paidon: formData.slcm_paidon,
            slcm_receiptno: formData.slcm_receiptno,
            edtec365_servicerequest: serviceRequest?.id,
            slcm_documentlink: documentUrl,
        };

        createPaymentRecord(paymentData);
    };

    useEffect(() => {
        if (!attributeId || !isRecordCreated) return;

        if (isRecordCreated) {
            setFieldValue(attributeId, "");
        } else {
            const fieldError = isChecklistValid();
            setFieldValue(attributeId, fieldError ? false : "");
        }
        // eslint-disable-next-line
    }, [paymentMode, isRecordCreated]);

    const isChecklistValid = useCallback(() => {
        if (!serviceRequest || !isRecordCreated) return;

        if (serviceRequest.paymentStage !== ServiceRequestPaymentStage.Completed) {
            return "Payment is not completed.";
        }

        // eslint-disable-next-line
    }, [isRecordCreated, serviceRequest]);

    if (!user || !service || !serviceRequest || !service.paidService) {
        return <></>;
    }

    const amount = service.amount || 0;

    return (
        <div className={clsx(className, "d-flex flex-column gap-1 my-5")}>
            <div className="container mt-3">
                <h5 className="mb-3">Offline Payment Details</h5>
                <div className="row g-3">
                    <div className="col-md-4">
                        <label htmlFor="slcm_amount" className="form-label fw-semibold required">
                            Amount
                        </label>
                        <input
                            type="number"
                            className="form-control"
                            id="slcm_amount"
                            name="slcm_amount"
                            value={amount}
                            onChange={handleChange}
                            disabled={isReadonly}
                            placeholder="Enter Amount"
                            required={true}
                        />
                        {errors.slcm_amount && <div className="text-danger">{errors.slcm_amount}</div>}
                    </div>

                    <div className="col-md-4">
                        <label htmlFor="slcm_paidon" className="form-label fw-semibold required">
                            Date
                        </label>
                        <input
                            type="date"
                            className="form-control"
                            id="slcm_paidon"
                            name="slcm_paidon"
                            value={formData.slcm_paidon}
                            onChange={handleChange}
                            disabled={isReadonly}
                            required={true}
                        />
                        {errors.slcm_paidon && <div className="text-danger">{errors.slcm_paidon}</div>}
                    </div>

                    <div className="col-md-4">
                        <label htmlFor="slcm_receiptno" className="form-label fw-semibold required">
                            Receipt No
                        </label>
                        <input
                            type="text"
                            className="form-control"
                            id="slcm_receiptno"
                            name="slcm_receiptno"
                            value={formData.slcm_receiptno}
                            onChange={handleChange}
                            disabled={isReadonly}
                            placeholder="Enter Receipt No"
                            required={true}
                        />
                        {errors.slcm_receiptno && <div className="text-danger">{errors.slcm_receiptno}</div>}
                    </div>
                </div>

                <div className="mt-3">
                    <label htmlFor="attachment" className="form-label fw-semibold required">
                        Attach Document
                    </label>
                    <input
                        type="file"
                        id="attachment"
                        name="attachment"
                        className="form-control"
                        onChange={handleInputChange}
                        disabled={isReadonly}
                    />
                    {errors.attachment && <div className="text-danger">{errors.attachment}</div>}
                    {isUploading && <Spinner className='me-2' show={isUploading} />}
                </div>

                <div className="mt-4">
                    {!isConfirmHidden && (
                        <button type='button'
                            onClick={handleConfirm}
                            disabled={loading || isUploading} 
                            className='btn btn-lg btn-primary me-3'>
                            <span className='indicator-label'>
                                {loading && <Spinner className='me-2' show={loading} />}
                                <span>Confirm</span>

                                <KTSVG
                                    path='/media/icons/duotune/arrows/arr064.svg'
                                    className='svg-icon-3 ms-2 me-0'
                                />
                            </span>
                        </button>
                    )}
                    {statusMessage && <p className="text-success mt-3">{statusMessage}</p>}
                </div>
            </div>
        </div>
    );
};


const PaymentStatus = ({ serviceRequest, amount, className }: {
    serviceRequest: IServiceRequest,
    amount: number,
    className?: string,
}) => {
    const getStatus = () => {
        switch (serviceRequest.paymentStage) {
            case ServiceRequestPaymentStage.Completed:
                return "Payment Received.";
            case ServiceRequestPaymentStage.OnGoing:
            case ServiceRequestPaymentStage.Pending:
                return "Waiting for confirmation from the bank.";
        }
    }

    return <>
        <div className={clsx(className, "d-flex align-items-center gap-1")}>
            <span className="fs-2 fw-bolder text-gray-800">{formatMoney(amount)}</span>
            <div><span className="badge badge-info">{getStatus()}</span></div>
        </div>
    </>
}

const formatMoney = (number?: number | string) => {
    if (!number) number = 0;

    if (typeof number !== 'number') {
        number = Number(number)
    }

    const formatter = new Intl.NumberFormat(navigator.language, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });

    return formatter.format(number);
}



// TODO
//1. payment table se data show karna he jb service request draft mode me open ho tb

